// src/components/clients/ClientManagement.js
import React, { useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Card, Table, Badge, Button, Modal, FormField, Input, SearchInput, ConfirmDialog, ResponsiveGrid } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';

export default function ClientManagement() {
  const { clients, addClient, updateClient, deleteClient, trucks, users } = useApp();
  const { isMobile } = useResponsive();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewClient, setViewClient] = useState(null);
  const [form, setForm] = useState({ name: '', contact: '', email: '', phone: '', address: '' });

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.contact?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setEditClient(null);
    setForm({ name: '', contact: '', email: '', phone: '', address: '' });
    setModalOpen(true);
  };

  const openEdit = (client) => {
    setEditClient(client);
    setForm({ name: client.name, contact: client.contact || '', email: client.email || '', phone: client.phone || '', address: client.address || '' });
    setModalOpen(true);
  };

  const handleSave = () => {
    if (!form.name) return;
    if (editClient) {
      updateClient(editClient.id, form);
    } else {
      addClient(form);
    }
    setModalOpen(false);
  };

  const columns = [
    {
      key: 'name', label: 'Client',
      render: (val, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(16,185,129,0.12)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, fontFamily: 'Syne, sans-serif' }}>
            {val.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--textPrimary)' }}>{val}</div>
            <div style={{ fontSize: 11, color: 'var(--textFaint)' }}>{row.email}</div>
          </div>
        </div>
      )
    },
    { key: 'contact', label: 'Contact Person', render: v => <span style={{ color: 'var(--textSecondary)', fontSize: 12 }}>{v || '—'}</span> },
    { key: 'phone', label: 'Phone', render: v => <span style={{ color: 'var(--textMuted)', fontSize: 12 }}>{v || '—'}</span> },
    { key: 'address', label: 'Address', render: v => <span style={{ color: 'var(--textMuted)', fontSize: 12, maxWidth: 180, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v || '—'}</span> },
    {
      key: 'truckIds', label: 'Trucks',
      render: (v) => <Badge color="#3b82f6">{v?.length || 0} trucks</Badge>
    },
    {
      key: 'status', label: 'Status',
      render: v => <Badge color={v === 'active' ? '#10b981' : '#6b7280'}>{v}</Badge>
    },
    { key: 'createdAt', label: 'Since', render: v => <span style={{ fontSize: 12, color: 'var(--textFaint)' }}>{v}</span> },
    {
      key: 'id', label: 'Actions',
      render: (id, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); setViewClient(row); }}>View</Button>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); setConfirmDelete(row); }}>Del</Button>
        </div>
      )
    },
  ];

  const clientTrucks = viewClient ? trucks.filter(t => t.clientId === viewClient.id) : [];
  const clientUser = viewClient ? users.find(u => u.clientId === viewClient.id) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, alignItems: isMobile ? 'stretch' : 'center', marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search clients..." />
          </div>
          <Button onClick={openAdd} icon="＋">Add Client</Button>
        </div>

        <ResponsiveGrid columns={3} tabletColumns={2} mobileColumns={1} gap={12} style={{ marginBottom: 20 }}>
          <div style={summaryCard}>
            <div style={summaryLabel}>Total Clients</div>
            <div style={summaryValue}>{clients.length}</div>
          </div>
          <div style={summaryCard}>
            <div style={summaryLabel}>Active</div>
            <div style={{ ...summaryValue, color: '#10b981' }}>{clients.filter(c => c.status === 'active').length}</div>
          </div>
          <div style={summaryCard}>
            <div style={summaryLabel}>Total Trucks Assigned</div>
            <div style={{ ...summaryValue, color: '#3b82f6' }}>{clients.reduce((acc, c) => acc + (c.truckIds?.length || 0), 0)}</div>
          </div>
        </ResponsiveGrid>

        <Table columns={columns} data={filtered} emptyMessage="No clients found" />
      </Card>

      {/* Add/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editClient ? 'Edit Client' : 'Add New Client'} width={520}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FormField label="Company / Client Name" required>
            <Input value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="e.g. Pacific Cargo Inc." required />
          </FormField>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <FormField label="Contact Person">
              <Input value={form.contact} onChange={v => setForm(f => ({ ...f, contact: v }))} placeholder="Full name" />
            </FormField>
            <FormField label="Phone">
              <Input value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} placeholder="+63 9XX XXX XXXX" />
            </FormField>
          </div>
          <FormField label="Email Address">
            <Input value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} placeholder="contact@company.com" type="email" />
          </FormField>
          <FormField label="Address">
            <Input value={form.address} onChange={v => setForm(f => ({ ...f, address: v }))} placeholder="Full business address" />
          </FormField>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSave}>{editClient ? 'Save Changes' : 'Create Client'}</Button>
        </div>
      </Modal>

      {/* View Detail Modal */}
      <Modal open={!!viewClient} onClose={() => setViewClient(null)} title="Client Profile" width={560}>
        {viewClient && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, padding: '16px', background: 'var(--bgInput)', borderRadius: 12, border: '1px solid var(--borderInput)' }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(16,185,129,0.15)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, fontFamily: 'Syne, sans-serif' }}>
                {viewClient.name.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 18, color: 'var(--textPrimary)' }}>{viewClient.name}</div>
                <div style={{ fontSize: 13, color: 'var(--textMuted)', marginTop: 2 }}>{viewClient.email}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Badge color="#10b981">{viewClient.status}</Badge>
                  <Badge color="#3b82f6">{clientTrucks.length} trucks</Badge>
                  {clientUser && <Badge color="#f97316">Has portal access</Badge>}
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'Contact', value: viewClient.contact },
                { label: 'Phone', value: viewClient.phone },
                { label: 'Address', value: viewClient.address },
                { label: 'Since', value: viewClient.createdAt },
              ].map(item => (
                <div key={item.label} style={{ padding: '10px 14px', background: 'var(--bgInput)', borderRadius: 8, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 10, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{item.label}</div>
                  <div style={{ fontSize: 13, color: 'var(--textSecondary)' }}>{item.value || '—'}</div>
                </div>
              ))}
            </div>

            {clientTrucks.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Assigned Trucks</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {clientTrucks.map(t => (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bgInput)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: t.color }} />
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--textPrimary)' }}>{t.plateNumber}</div>
                          <div style={{ fontSize: 11, color: 'var(--textFaint)' }}>{t.model} · {t.driver}</div>
                        </div>
                      </div>
                      <Badge color={t.status === 'moving' ? '#10b981' : t.status === 'idle' ? '#f59e0b' : '#ef4444'}>{t.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => deleteClient(confirmDelete.id)}
        title="Delete Client"
        message={`Are you sure you want to delete "${confirmDelete?.name}"? All associated data will be unlinked.`}
      />
    </div>
  );
}

const summaryCard = { background: 'var(--bgInput)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px' };
const summaryLabel = { fontSize: 10, fontWeight: 700, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 4 };
const summaryValue = { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 28, color: 'var(--textPrimary)' };
